import { useCallback, useMemo, useState } from "react"
import type { RefObject } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import type { SxProps, Theme } from "@mui/material"

import type { UserOrgRole } from "types/users.types"
import type { USER_ROLES } from "helpers/utils/constants"

import { selectCurrentOrgName } from "features/store/orgSlice"
import { formatFilename } from "helpers/utils/common"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"
import DataGridTable from "widgets/common/DataGridTable"
import usePermissions from "helpers/hooks/usePermissions"
import { getUsersTableColumns } from "./helpers"
import EditUserModal from "./EditUserModal"
import RemoveUserModal from "./RemoveUserModal"

interface UsersTableProps {
  users: UserOrgRole[] | undefined
  isLoading: boolean
  isFetching?: boolean
  titleKey?: string
  sx?: SxProps<Theme>
  scrollAnchorRef?: RefObject<HTMLElement | null>
}

const UsersTable = ({
  users = [],
  isLoading,
  isFetching,
  titleKey,
  sx = {},
  scrollAnchorRef,
}: UsersTableProps) => {
  const { t } = useTranslation()
  const { isOwner } = usePermissions()
  const orgName = useSelector(selectCurrentOrgName)
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false)
  const [rowUsername, setRowUsername] = useState<string>("")
  const [rowRole, setRowRole] = useState<keyof typeof USER_ROLES>("editor")

  const handleOpenEdit = useCallback(
    (username: string, role: keyof typeof USER_ROLES) => {
      setRowUsername(username)
      setRowRole(role)
      setOpenEditModal(true)
    },
    [],
  )
  const handleCloseEdit = useCallback(() => setOpenEditModal(false), [])

  const handleOpenRemove = useCallback((username: string) => {
    setRowUsername(username)
    setOpenRemoveModal(true)
  }, [])
  const handleCloseRemove = useCallback(() => setOpenRemoveModal(false), [])

  const columns = useMemo(
    () => getUsersTableColumns(t, handleOpenEdit, handleOpenRemove, isOwner),
    [t, handleOpenEdit, handleOpenRemove, isOwner],
  )

  return (
    <>
      <SkeletonWrapper
        isWaiting={isLoading || Boolean(isFetching)}
        height={540}
        width={"100vw"}
      >
        <DataGridTable
          sx={sx}
          title={titleKey}
          rows={users}
          columns={columns}
          getRowId={(user) => user.username}
          sortModel={[{ field: "username", sort: "asc" }]}
          csvFileName={formatFilename(`${orgName}_${t("generic.USERS")}`, "csv")}
          scrollAnchorRef={scrollAnchorRef}
        />
      </SkeletonWrapper>
      <EditUserModal
        open={openEditModal}
        onClose={handleCloseEdit}
        username={rowUsername}
        role={rowRole}
      />
      <RemoveUserModal
        open={openRemoveModal}
        onClose={handleCloseRemove}
        username={rowUsername}
      />
    </>
  )
}

export default UsersTable
